import React, { ChangeEvent, useEffect, useRef, useState } from "react";

import { useClickOutside } from "@/Common/CustomHooks/useClickOutside";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { LinkButton } from "@/DesignSystem";
import { RiskFactorCategoryCode, RiskFactorCode } from "@/Patient/Models/MedicalSummary/RiskFactorCode";

import styles from "./RiskFactorAutoComplete.module.scss";

interface IRiskFactorAutocomplete {
    onItemSelectCallback: (item: RiskFactorValue) => void;
    onBlurCallback?: (text: string) => void;
    onClickOutside?: (event?: EventTarget) => void;
    valid?: boolean;
    filter?: RiskFactorCategoryCode[];
}

interface RiskFactorValue {
    Id: string;
    Category: string;
    CategoryId: number;
    Text: string;
}

function RiskFactorAutocomplete(props: Readonly<IRiskFactorAutocomplete>): JSX.Element {
    const [forceHideList, setForceHideList] = useState(false);
    const [text, setText] = useState("");
    const [riskFactors, setRiskFactors] = useState<RiskFactorValue[][]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const cancelBlur = useRef<boolean>(false);
    const triggerFocus = useRef<boolean>(false);

    const { getText } = useTranslation();

    useEffect(() => {
        let riskFactors: RiskFactorValue[][] = [];

        for (let key of Object.keys(cacheStore.RiskFactorCodeCache)) {
            if (!props.filter || props.filter.includes(key as RiskFactorCategoryCode)) {
                riskFactors.push(cacheStore.RiskFactorCodeCache[key]
                    .sortByProperty(p => p.Position)
                    .map((p: RiskFactorCode) => ({
                        Id: p.Id,
                        Category: p.Category,
                        CategoryId: p.CategoryId,
                        Text: getText(p.VocabularyResourceKey)
                    })));
            }
        }

        setRiskFactors(riskFactors.sortByProperty(p => p[0].CategoryId));
    }, [props.filter]);

    useClickOutside([containerRef, inputRef], target => {
        setForceHideList(true);

        if (triggerFocus.current)
            props.onClickOutside?.(target);

        // Only want to trigger the onClickOutside once
        // after the user has clicked on the input field.
        triggerFocus.current = false;
    });

    function onTextChange(e: ChangeEvent<HTMLInputElement>): void {
        const val = e.target.value;
        setForceHideList(val === "");
        setText(val);
        triggerFocus.current = false;
    }

    function onClick(): void {
        setForceHideList(false);
        // Reset the triggerFocus flag to true so the next time the user clicks outside the component
        // it will trigger the onClickOutside callback.
        triggerFocus.current = true;
    }

    const inputKeyDown = (e: React.KeyboardEvent): void => {
        // enter
        if (e.keyCode === 13 || e.keyCode === 9) {
            setForceHideList(true);
            onBlur();
            triggerFocus.current = false;
        }

        // escape
        if (e.keyCode === 27) {
            setForceHideList(true);
            onBlur();
            triggerFocus.current = false;
        }
    };

    function selectItem(item: RiskFactorValue): void {
        setText("");
        setForceHideList(false);
        triggerFocus.current = true;
        props.onItemSelectCallback(item);
    }

    function onBlur(): void {
        if (cancelBlur.current)
            return;

        if (text) {
            setForceHideList(true);
            props.onBlurCallback?.(text);
            setText("");
        }
    }

    return (
        <div className={styles.container}>
            <input
                ref={inputRef}
                tabIndex={0}
                data-cy="risk-factor-input"
                onKeyDown={inputKeyDown}
                type="text"
                onChange={onTextChange}
                onClick={onClick}
                onBlur={onBlur}
                value={text}
                className={props.valid === false ? "form-control invalid" : "form-control"}
            />

            {riskFactors != null && !forceHideList && (
                <div
                    className={styles.listContainer}
                    ref={containerRef}
                    onMouseEnter={() => (cancelBlur.current = true)}
                    onMouseLeave={() => (cancelBlur.current = false)}
                >
                    <ul
                        data-cy="risk-factor-list"
                        className={styles.riskfactorList}
                    >
                        {riskFactors.map(riskFactorsByCategory => (
                            <li key={`riskFactorRow-${riskFactorsByCategory[0].CategoryId}`}>
                                {riskFactorsByCategory.map((riskFactorItem: RiskFactorValue, idx2) => (
                                    <span key={`riskFactorItem-${riskFactorsByCategory[0].CategoryId}-${riskFactorItem.Id}`}>
                                        <LinkButton
                                            dataCy="risk-factor-code"
                                            onClick={() => selectItem(riskFactorItem)}
                                            label={riskFactorItem.Text}
                                            underline={false}
                                        />
                                        {idx2 < riskFactorsByCategory.length - 1 && <span>; </span>}
                                    </span>
                                ))}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export { RiskFactorAutocomplete, RiskFactorValue };
